import { useState, useMemo, useEffect } from "react";
import { LazyMotion, m, useIsPresent } from "framer-motion";
import DonationOption from "./Fragments/DonationOption";
import { Button } from "components/Button";
import SubmitIcon from "public/icons/submit-icon.svg";
const loadFeatures = () =>
  import("utilities/framerFeatures.js").then((res) => res.default);

interface PledgeFormProps {
  variant?: string;
}
interface OptionType {
  id?: string;
  amount: number;
  frequency: string;
  label: string;
}

const PledgeForm: React.FC<PledgeFormProps> = ({ variant }) => {
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [activeTab, setActiveTab] = useState<
    "3-months" | "6-months" | "12-months"
  >("12-months");
  const [animalName, setAnimalName] = useState("");
  const [animalId, setAnimalId] = useState("");
  const [surpriseMe, setSurpriseMe] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const animalIdParam = queryParams.get("animalId");
    const animalNameParam = queryParams.get("animalName");

    if (animalIdParam) {
      setAnimalId(animalIdParam);
    }
    if (animalNameParam) {
      setAnimalName(animalNameParam);
    }
  }, []);

  const handleOptionClick = (option: any) => {
    setSelectedOption(option);
  };

  const handleTabClick = (tab: "3-months" | "6-months" | "12-months") => {
    setActiveTab(tab);
  };

  // const handleSubmit = (e: any) => {
  //   e.preventDefault();
  //   // take selected option id and redirect to checkout page https://buy.stripe.com/[selectedOption.id]
  //   if (selectedOption && selectedOption.id !== "") {
  //     window.open(`https://buy.stripe.com/${selectedOption?.id}`, "_blank");
  //   }
  // };

  // Options are $12, $30, $55
  const options = useMemo(() => {
    return activeTab === "3-months"
      ? [
          {
            id: "price_1OWlYgHK6Gicgl1aaXCjU6wp",
            amount: 12,
            frequency: "3-months",
            label: "$12",
          },
          {
            id: "price_1OWnJKHK6Gicgl1aDOLhHGbz",
            amount: 30,
            frequency: "3-months",
            label: "$30",
          },
          {
            id: "price_1OWnMtHK6Gicgl1a0UYXTth4",
            amount: 55,
            frequency: "3-months",
            label: "$55",
          },
          {
            id: "price_1OjTtTHK6Gicgl1ahEiCTIMa",
            amount: 100,
            frequency: "3-months",
            label: "$100",
          },
        ]
      : activeTab === "6-months"
      ? [
          {
            id: "price_1OWlZXHK6Gicgl1aPEbxwgIC",
            amount: 12,
            frequency: "6-months",
            label: "$12",
          },
          {
            id: "price_1OWnKMHK6Gicgl1abmO9BsOH",
            amount: 30,
            frequency: "6-months",
            label: "$30",
          },
          {
            id: "price_1OWnNaHK6Gicgl1a7rLcLEPR",
            amount: 55,
            frequency: "6-months",
            label: "$55",
          },
          {
            id: "price_1OjTvfHK6Gicgl1aiG8Z3dvh",
            amount: 100,
            frequency: "6-months",
            label: "$100",
          },
        ]
      : [
          {
            id: "price_1OWmMMHK6Gicgl1achM3hvad",
            amount: 12,
            frequency: "12-months",
            label: "$12",
          },
          {
            id: "price_1OWnL7HK6Gicgl1a0VKL7ZB6",
            amount: 30,
            frequency: "12-months",
            label: "$30",
          },
          {
            id: "price_1OWnO0HK6Gicgl1ajH9kIKhf",
            amount: 55,
            frequency: "12-months",
            label: "$55",
          },
          {
            id: "price_1OjTwiHK6Gicgl1aFIaRn7OE",
            amount: 100,
            frequency: "12-months",
            label: "$100",
          },
        ];
  }, [activeTab]);

  let isPresent = useIsPresent();

  const handleSurpriseMeChange = (e: any) => {
    setSurpriseMe(e.target.checked);
  };

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col bg-white">
      <div className="pb-6">
        <h3 className="mb-2 text-center font-heading text-xl font-medium">
          I'd Like To Virtually Foster...
        </h3>
        <div className="mb-4 flex flex-row items-center justify-center gap-4">
          <input
            name="visualName"
            className="w-full rounded-full border border-primary px-4 py-4"
            placeholder="Animal Name"
            value={animalName}
            onChange={(e) => setAnimalName(e.target.value)}
          />
          <input
            name="visualId"
            className="w-full rounded-full border border-primary px-4 py-4"
            placeholder="Animal ID"
            value={animalId}
            onChange={(e) => setAnimalId(e.target.value)}
          />
        </div>
        <div className="flex flex-row items-center justify-center gap-4">
          <input id="surpriseMe" type="checkbox"  onChange={handleSurpriseMeChange} />{" "}
          <label htmlFor="surpriseMe">
            I don't have a particular dog picked out &mdash; I want to support
            them all!
          </label>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex w-full flex-col justify-center gap-6 overflow-hidden">
        <div className={`overflow-hidden rounded-full font-body font-bold`}>
          <button
            className={`${
              activeTab === "3-months"
                ? "bg-primary text-white"
                : "bg-white text-primary"
            } w-1/3 rounded-bl-full rounded-tl-full border border-primary px-4 py-4 transition-all duration-200 ease-in-out hover:underline`}
            onClick={() => handleTabClick("3-months")}
          >
            3 Months
          </button>
          <button
            className={`${
              activeTab === "6-months"
                ? "bg-primary text-white"
                : "bg-white text-primary"
            } w-1/3 border-b border-t border-primary px-4 py-4 transition-all duration-200 ease-in-out hover:underline`}
            onClick={() => handleTabClick("6-months")}
          >
            6 Months
          </button>
          <button
            className={`${
              activeTab === "12-months"
                ? "bg-primary text-white"
                : "bg-white text-primary"
            } w-1/3 rounded-br-full rounded-tr-full border border-primary px-4 py-4 transition-all duration-200 ease-in-out hover:underline`}
            onClick={() => handleTabClick("12-months")}
          >
            12 Months
          </button>
        </div>
        <form
          action="/api/stripe/create-payment-link"
          method="POST"
          encType="application/x-www-form-urlencoded"
          className={`flex flex-col items-center gap-4`}
        >
          <input type="hidden" name="animalName" value={animalName} />
          <input type="hidden" name="animalId" value={animalId} />
          <input type="hidden" name="surpriseMe" value={surpriseMe ? "yes" : "no"} />
          <div
            className={`grid h-fit w-full grid-cols-2 gap-4 overflow-hidden px-4 md:px-12`}
          >
            <LazyMotion features={loadFeatures}>
              {options.map((option, index) => (
                <m.div
                  key={option?.id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    opacity: { duration: 0.5 },
                  }}
                  style={{
                    position: isPresent ? "relative" : "absolute",
                    top: isPresent ? 0 : -100,
                    left: isPresent ? 0 : -100,
                    originX: 0,
                  }}
                >
                  <DonationOption
                    key={index}
                    option={option}
                    isActive={selectedOption?.id === option?.id}
                    onClick={() => handleOptionClick(option)}
                  />
                </m.div>
              ))}
            </LazyMotion>
          </div>
          <Button
            variant="secondary-outline"
            type="submit"
            className="group relative flex w-full max-w-md items-center justify-center py-4"
            disabled={!selectedOption}
          >
            <span>Submit</span>
            <SubmitIcon className="absolute right-2" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PledgeForm;
